import { Inject, Injectable, OnDestroy } from '@angular/core';

import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { BehaviorSubject, Observable, Subject, filter, from, map, share, startWith, switchMap, take, takeUntil, timeout, zip } from 'rxjs';

import { CommonsConfigEnvironment, CONFIG_ENVIRONMENT, JobStatus } from '@mirage-projects/cw-models';

import { MagnetService } from '../../abstract-classes/magnet.service';
import { AuthorizationService } from '../authorization/authorization.service';
import { LogService } from '../log';
import {
    NotificationEventType,
    NotificationObjectType,
    NotificationsSettingsService,
} from '../notification-settings/notifications-settings.service';
import { ToastService } from '../toast/toast.service';
import { TranslationService } from '../translate/translation.service';
import { HAS_TO_SHOW_NOTIFICATIONS_TOKEN } from './has-to-show-notification-token';

export type NotificationData = {
    name: NotificationEventType;
    catalogObjectType: NotificationObjectType;
    quotaType: string;
    callerUserId: string;
    jobStatus: JobStatus;
    thresholdName: string;
    catalogObjectName: string;
    jobId?: number;
    catalogObjectId?: number;
};

export type NotificationMessage = { type: string; data: NotificationData };

export enum NotificationTopics {
    CATEGORIES = 'CATALOG/categories/',
    LAYERS = 'CATALOG/layers/',
    PROJECTS = 'CATALOG/projects/',
    SITES = 'CATALOG/sites/',
    QUOTA = 'QUOTA/',
}

/**
 * Service to manage the backend websocket notifications
 */
@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
    messages$: Observable<NotificationMessage> | undefined;
    jobFinishedMessages$: Observable<NotificationData> | undefined;
    jobStartedMessages$: Observable<NotificationData> | undefined;
    notificationMessages$: Observable<NotificationData> | undefined;

    /** Output setTopics stream */
    private setTopics$ = new Subject<string[]>();
    /** Input setTopics ack stream */
    private setTopicsAck$ = new Subject<string[]>();

    private serverNotifications$!: Observable<NotificationData>;
    private socket!: Socket;
    private notificationTopics: string[] = [];
    private defaultNotificationTopics: NotificationTopics[] = [
        NotificationTopics.CATEGORIES,
        NotificationTopics.LAYERS,
        NotificationTopics.PROJECTS,
        NotificationTopics.SITES,
    ];
    private readonly jobNameTypes = ['JOB_FINISHED', 'JOB_STARTED'];
    private readonly notificationHideDelay = 5000;
    private readonly onDestroy$ = new Subject<void>();

    constructor(
        @Inject(CONFIG_ENVIRONMENT) private configEnvironment: CommonsConfigEnvironment,
        private translationService: TranslationService,
        private authorizationService: AuthorizationService,
        private toastService: ToastService,
        private magnetService: MagnetService,
        private notificationsSettingsService: NotificationsSettingsService,
        private logService: LogService,
        @Inject(HAS_TO_SHOW_NOTIFICATIONS_TOKEN) private hasToShowNotifications = true
    ) {
        if (!this.isValidUser()) {
            return;
        }

        this.socket = this.initSocket();

        this.initSetTopicsListener();
        this.initTopics();

        this.initSocketListener();
        this.initMessageChannels();
        this.initToastSubscription();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    /**
     * Subscribe to topic
     * @param topic topic name
     */
    addTopicNotification(topic: string): void {
        this.notificationTopics.push(topic);
        this.emitTopics();
    }

    /**
     * Remove subscription from topic
     * @param topicToRemove Remove topic subscription
     */
    removeTopicNotification(topicToRemove: string): void {
        const indexToRemove = this.notificationTopics.findIndex((topic) => topic === topicToRemove);
        this.notificationTopics.splice(indexToRemove, 1);

        this.emitTopics();
    }

    /**
     * Remove all subscriptions topics
     */
    removeAllTopics(): void {
        this.notificationTopics.length = 0;
        this.emitTopics();
    }

    /**
     * Add default subscription topics
     */
    addDefaultTopics(): void {
        this.notificationTopics = this.notificationTopics.concat(this.defaultNotificationTopics);
        this.emitTopics();
    }

    /**
     * Set and emmit new notification topics
     * @param topics The topics to set
     */
    setTopicsNotification(topics: string[]): void {
        this.notificationTopics = topics;
        this.emitTopics();
    }

    /**
     * Get current notification topics
     */
    getTopicsNotification(): string[] {
        return this.notificationTopics;
    }

    private initSocket(): Socket {
        const config: SocketIoConfig = {
            url: this.configEnvironment.notifications,
            options: {
                path: '/notifications/ws',
                transports: ['websocket'],
                upgrade: false,
                query: {
                    t: this.authorizationService.getToken(),
                },
            },
        };

        return new Socket(config);
    }

    private isValidUser(): boolean {
        return !this.authorizationService.isCurrentUserAnonymous();
    }

    private initSocketListener(): void {
        this.serverNotifications$ = this.socket.fromEvent<NotificationData>('notification').pipe(
            takeUntil(this.onDestroy$),
            filter((data) => {
                const isCurrentUser = this.isCurrentUser(data.callerUserId);
                const isJobValidType = this.isJobValidType(data.name);

                return !isCurrentUser || isJobValidType;
            }),
            share()
        );
    }

    private initMessageChannels(): void {
        this.messages$ = this.serverNotifications$.pipe(
            switchMap((data) => {
                return from(this.buildMessages(data));
            })
        );

        this.jobFinishedMessages$ = this.selectMessageChannel('notification:job_finished');
        this.jobStartedMessages$ = this.selectMessageChannel('notification:job_started');
        this.notificationMessages$ = this.selectMessageChannel('notification');

        this.socket
            .fromEvent<NotificationData>('reconnect')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => this.emitTopics());
    }

    private initToastSubscription(): void {
        const toastEvent$ = this.serverNotifications$.pipe(
            filter((data) => {
                const isJobStarted = data.name === 'JOB_STARTED';
                const hasNotifications = this.notificationsSettingsService.getAllNotificationPreferencesFromObject('all');
                const hasSpecificNotification = this.notificationsSettingsService.getNotificationPreference(
                    data.name,
                    data.catalogObjectType
                );

                return !isJobStarted && (hasNotifications || hasSpecificNotification);
            })
        );
        toastEvent$.subscribe((data) => this.sendToast(data));
    }

    private selectMessageChannel(type: string): Observable<NotificationData> | undefined {
        return this.messages$?.pipe(
            filter((msg) => msg.type === type),
            map((msg) => msg.data)
        );
    }

    /**
     * This method manages the emission of topics one at a time,
     * waiting for the previous message ack to send the next one.
     * Will timeout after 5 seconds to avoid blocking the queue.
     * Will ignore out-of-sync acks not corresponding to the previous message.
     */
    private initSetTopicsListener(): void {
        const waitingMessage$ = new BehaviorSubject<string[]>([]);

        // Arbitray value just to don't let the queue in a blocked state
        const ackTimeout = 5000;

        // We add start value, timeout and filter to inital setTopicsAck$ stream
        const setTopicsAck$ = this.setTopicsAck$.pipe(
            // To trigger first topics message, with fake ack
            startWith(null),
            timeout({
                each: ackTimeout,
                // Timeout sending ack with waitingMessage value to pass the filter
                with: () => waitingMessage$.pipe(take(1)),
            }),
            // If it's null (first message) or is the ack for the waiting message then continue.
            filter((ackTopics) => ackTopics === null || ackTopics === waitingMessage$.value)
        );

        // If we have topics to send and the previous ack, send the topics
        zip([this.setTopics$, setTopicsAck$])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(([topics]) => {
                waitingMessage$.next(topics);
                this.emitTopicsToSocket(topics);
            });
    }

    private initTopics(): void {
        const isFromMagnet = this.magnetService.isConnectingFromMagnet();
        const isAdmin = this.authorizationService.isUserLoggedAdmin();
        if (isFromMagnet && isAdmin) {
            this.defaultNotificationTopics.push(NotificationTopics.QUOTA);
        }

        this.addDefaultTopics();
    }

    private sendToast(data: NotificationData): void {
        if (this.hasToShowNotifications) {
            const name = !data?.quotaType ? data?.catalogObjectName : '';
            this.showNotificationToast(data, name);
        }
    }

    private emitTopics(): void {
        // Make each topics message unique
        const topics = [...this.notificationTopics];
        this.setTopics$.next(topics);
    }

    private emitTopicsToSocket(topics: string[]): void {
        if (!this.socket) {
            return;
        }

        this.socket.emit(
            'setTopics',
            {
                topics,
            },
            (ack: unknown) => {
                this.setTopicsAck$.next(topics);
                this.logService.debug('Notification socket acknowledgement:', ack);
            }
        );
    }

    private buildMessages(data: NotificationData): NotificationMessage[] {
        const messages = [];
        if (this.isJobValidType(data.name)) {
            const type = `notification:${data.name.toLowerCase()}`;

            messages.push({ type, data });
        }

        const isJobStarted = data.name === 'JOB_STARTED';

        const isNotificationType = data.catalogObjectType !== 'categories' && !data.quotaType;
        if (!isJobStarted && isNotificationType) {
            const type = 'notification';
            messages.push({ type, data });
        }

        return messages;
    }

    private isCurrentUser(callerUserId: string): boolean {
        return this.authorizationService.userLogged.userId === callerUserId;
    }

    private isJobValidType(typeName: string): boolean {
        return this.jobNameTypes.includes(typeName);
    }

    private showNotificationToast(data: NotificationData, name: string, hideCloseButton = false): void {
        const translate = this.getNotificationMessage(data);
        const message = this.translationService.translate(translate, { name });

        this.toastService.generic(message, this.notificationHideDelay, !hideCloseButton);
    }

    private getNotificationMessage(data: NotificationData): string {
        if (data.jobStatus) {
            return `NOTIFICATION_SERVICE.${data.catalogObjectType}: ${data.name}: ${data.jobStatus}`;
        }
        if (data.quotaType) {
            return `NOTIFICATION_SERVICE.${data.name}: ${data.thresholdName}: ${data.quotaType}`;
        }
        return `NOTIFICATION_SERVICE.${data.catalogObjectType}: ${data.name}`;
    }
}
